import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';

/**
 * signage42 — "scala" theme.
 *
 * A cream/gold palette evoking an opera house programme, defined equally for
 * both schemes. Light and dark are two renderings of the same product, not a
 * designed dark theme with a light fallback: `colorScheme.light` carries its
 * own warm surface scale so PrimeVue never falls through to Aura's cold slate
 * greys, and `colorScheme.dark` carries the near-black warm scale.
 *
 * Glass-surface primitives live in CSS (src/style.css + Tailwind plugins);
 * this preset is the PrimeVue design-token layer that every component picks
 * up automatically. `darkModeSelector` is '.app-dark', which is nested onto
 * signage preview stages as well as set on <html> — see the header comment in
 * src/style.css before changing anything scheme-related.
 *
 * Contrast targets (WCAG AA): body text ≥ 7:1 on the scheme's own card
 * surface, mid-tone text ≥ 4.5:1. Accent tokens are used as focus rings and
 * interactive hover emphasis; never the only signal — every interactive
 * element also changes shape/weight.
 */
export const ScalaPreset = definePreset(Aura, {
  semantic: {
    // Honeyed gold. The bright end (300–500) is the readable one on dark; the
    // deep end (800+) is the readable one on cream. Each scheme picks its own.
    primary: {
      50: '#FDF9EC',
      100: '#FAF1CD',
      200: '#F5E3A1',
      300: '#EFD378',
      400: '#E9C461',
      500: '#E2B44E', // base
      600: '#C79A3A',
      700: '#A17929',
      800: '#7A5B1E',
      900: '#5A4217',
      950: '#342610',
    },
    colorScheme: {
      // Near-black with a faint warm undertone so it doesn't read cold/clinical.
      dark: {
        primary: {
          color: '{primary.300}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.200}',
          activeColor: '{primary.400}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400} 22%, transparent)',
          focusBackground: 'color-mix(in srgb, {primary.400} 32%, transparent)',
          color: '{primary.100}',
          focusColor: '{primary.50}',
        },
        surface: {
          0: '#FFFFFF',
          50: '#F6EEDC',
          100: '#E6DCC4',
          200: '#C6B895',
          300: '#9E9272',
          400: '#7E7660',
          500: '#5C5746',
          600: '#403C30',
          700: '#2B2820',
          800: '#1C1A15',
          900: '#141311',
          950: '#0B0A09',
        },
      },
      light: {
        // gold-800, not gold-600. Measured against the cream ground: gold-500
        // is 1.85:1, gold-600 2.48:1, gold-700 3.81:1, gold-800 6.28:1 on #FFFFFF
        // (5.88:1 on the #FBF7EE page ground). Only
        // gold-800 can carry text, or carry a white button label (#FFFFFF on
        // #7A5B1E is 6.28:1). Hover/active therefore step DOWN into 900/950
        // rather than up. Dark's primary is unchanged.
        primary: {
          color: '{primary.800}',
          contrastColor: '{surface.0}',
          hoverColor: '{primary.900}',
          activeColor: '{primary.950}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.500} 14%, transparent)',
          focusBackground: 'color-mix(in srgb, {primary.500} 20%, transparent)',
          color: '{primary.800}',
          focusColor: '{primary.900}',
        },
        // The warm cream neutral ramp. Without this, PrimeVue light mode fell
        // through to Aura's slate greys and the product read as a different
        // application. 0 is the card, 50 the page ground, 100 the sunken well;
        // 200 matches --lv-border. 500 is NOT tied to --lv-border-control: primeui
        // maps text-surface-500 onto it at ~70 call sites, so it has to clear 4.5:1
        // as body copy (#7E6F4C = 4.92:1 on white, 4.60:1 on the page ground).
        // The old #8A7A55 was a line colour promoted to text and measured 4.20:1.
        // Remaining ramp steps keep the
        // role tokens and the PrimeVue component tokens visually aligned.
        surface: {
          0: '#FFFFFF',
          50: '#FBF7EE',
          100: '#F3ECDD',
          200: '#E3D9C2',
          300: '#CFC2A4',
          400: '#A99C7C',
          500: '#7E6F4C',
          600: '#6B6047',
          700: '#4E4737',
          800: '#353025',
          900: '#221F19',
          950: '#14120E',
        },
      },
    },
  },
});
